import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Send } from "lucide-react";
import { Avatar, AvatarStack, Button, EmptyState, Spinner, Textarea, relativeTime, toast } from "@/ui";
import { api, useSession } from "../_shims";
import { useConversations } from "../lib/hooks";
import { PageFrame } from "../components/PageFrame";
import { BlockReport } from "../components/BlockReport";
import s from "../network.module.css";

function Thread({ conversationId }: { conversationId: string }) {
  const { user } = useSession();
  const qc = useQueryClient();
  const [text, setText] = useState("");
  const bottom = useRef<HTMLDivElement>(null);
  const convs = useConversations(false);
  const conv = convs.data?.conversations.find((c) => c.id === conversationId);
  const others = conv?.participants.filter((p) => p.userId !== user?.id) ?? [];
  const msgs = useQuery({
    queryKey: ["network", "messages", conversationId],
    queryFn: () => api.messages(conversationId),
    refetchInterval: () => (typeof document !== "undefined" && document.visibilityState === "visible" ? 15_000 : false),
  });
  const send = useMutation({
    mutationFn: (content: string) => api.sendMessage(conversationId, { content }),
    onSuccess: () => {
      setText("");
      qc.invalidateQueries({ queryKey: ["network", "messages", conversationId] });
      qc.invalidateQueries({ queryKey: ["network", "conversations"] });
    },
    onError: (e) => toast(e instanceof Error ? e.message : "Could not send.", "danger"),
  });
  useEffect(() => {
    bottom.current?.scrollIntoView({ block: "end" });
  }, [msgs.data?.messages.length]);
  // Opening a thread marks it read on the server side when it is fetched; refresh the unread counts.
  useEffect(() => {
    if (msgs.data) qc.invalidateQueries({ queryKey: ["network", "conversations"] });
  }, [msgs.data, qc]);

  const submit = () => {
    const content = text.trim();
    if (!content) return;
    send.mutate(content);
  };

  return (
    <div className={s.thread}>
      <div className={s.pageHeader} style={{ padding: "10px 16px" }}>
        <AvatarStack people={others.map((p) => ({ name: p.displayName, src: p.avatarUrl, seed: p.userId }))} size={28} />
        <div className="u-grow" style={{ minWidth: 0 }}>
          <div style={{ fontWeight: 600 }} className="u-truncate">
            {others.map((p) => p.displayName).join(", ") || "Conversation"}
          </div>
          {conv?.matchId && <div className={`${s.small} ${s.muted}`}>About an introduction</div>}
        </div>
        {others.length === 1 && <BlockReport userId={others[0].userId} name={others[0].displayName} conversationId={conv?.id} />}
      </div>
      <div className={s.threadBody} aria-live="polite">
        {msgs.isLoading ? (
          <Spinner />
        ) : (msgs.data?.messages.length ?? 0) === 0 ? (
          <div className={`${s.small} ${s.muted}`}>No messages yet. Say hello.</div>
        ) : (
          msgs.data!.messages.map((m) => (
            <div key={m.id} className={s.bubble} data-mine={m.senderUserId === user?.id ? "true" : undefined}>
              {m.content}
              <div className={s.bubbleTime}>{relativeTime(m.created)}</div>
            </div>
          ))
        )}
        <div ref={bottom} />
      </div>
      <div className={s.composer}>
        <Textarea
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder="Write a message. Enter sends, Shift+Enter adds a line."
          aria-label="Message"
          rows={1}
          onKeyDown={(e) => {
            if (e.key === "Enter" && !e.shiftKey) {
              e.preventDefault();
              submit();
            }
          }}
        />
        <Button variant="primary" icon={<Send size={14} />} onClick={submit} loading={send.isPending} disabled={!text.trim()}>
          Send
        </Button>
      </div>
    </div>
  );
}

export function MessagesPage() {
  const { conversationId } = useParams<{ conversationId: string }>();
  const navigate = useNavigate();
  const { user, isSignedIn } = useSession();
  const convs = useConversations(true);
  if (!isSignedIn) {
    return (
      <PageFrame title="Messages">
        <EmptyState title="Sign in to message people on Hub">Messages travel through the Hub server between Hub users. Hub never messages anyone for you.</EmptyState>
      </PageFrame>
    );
  }
  const list = convs.data?.conversations ?? [];
  return (
    <div className={s.page}>
      <div className={s.messagesLayout}>
        <div className={s.convList} aria-label="Conversations">
          <div className={s.pageHeader}>
            <div className={s.pageTitle}>Messages</div>
          </div>
          {convs.isLoading ? (
            <div style={{ padding: 16 }}>
              <Spinner />
            </div>
          ) : list.length === 0 ? (
            <div style={{ padding: 16 }} className={`${s.small} ${s.muted}`}>
              No conversations yet. Start one from an introduction or a profile.
            </div>
          ) : (
            list.map((c) => {
              const others = c.participants.filter((p) => p.userId !== user?.id);
              const name = others.map((p) => p.displayName).join(", ") || "Conversation";
              return (
                <div key={c.id} className={s.convRow} data-selected={c.id === conversationId ? "true" : undefined} role="button" tabIndex={0} onClick={() => navigate(`/messages/${c.id}`)} onKeyDown={(e) => (e.key === "Enter" ? navigate(`/messages/${c.id}`) : undefined)}>
                  <Avatar name={name} src={others[0]?.avatarUrl} size={36} seed={others[0]?.userId ?? c.id} />
                  <div style={{ minWidth: 0 }}>
                    <div style={{ fontWeight: c.unread > 0 ? 650 : 500 }} className="u-truncate">
                      {name}
                    </div>
                    <div className={`${s.small} ${s.muted} u-truncate`}>{c.lastMessage?.content ?? "No messages yet"}</div>
                  </div>
                  <div className="u-col" style={{ alignItems: "flex-end", gap: 4 }}>
                    <span className={`${s.small} ${s.muted}`}>{relativeTime(c.lastMessageAt)}</span>
                    {c.unread > 0 && <span className={s.unread}>{c.unread}</span>}
                  </div>
                </div>
              );
            })
          )}
        </div>
        {conversationId ? <Thread conversationId={conversationId} /> : <EmptyState title="Pick a conversation">Messages refresh every 15 seconds while this page is visible.</EmptyState>}
      </div>
    </div>
  );
}
